import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Alert, CircularProgress,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, Chip,
  FormControlLabel, Switch, TableSortLabel,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import api from '../services/api';

function formatBytes(n) {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatNumber(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}

function formatPct(curr, prior) {
  if (curr === null || curr === undefined || prior === null || prior === undefined || prior === 0) return null;
  const pct = ((curr - prior) / prior) * 100;
  return pct;
}

function pctLabel(pct) {
  if (pct === null) return '—';
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

function pctColor(pct) {
  if (pct === null) return 'text.secondary';
  if (pct > 1) return 'error.main';     // bandwidth up = cost up = red
  if (pct < -1) return 'success.main';
  return 'text.secondary';
}

function isoDateAdd(iso, daysOffset) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + daysOffset);
  return d.toISOString().split('T')[0];
}

function daysBetween(startIso, endIso) {
  const a = new Date(startIso + 'T00:00:00Z');
  const b = new Date(endIso + 'T00:00:00Z');
  return Math.floor((b - a) / (1000 * 60 * 60 * 24)) + 1; // inclusive
}

function firstOfMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const ACCOUNT_COLORS = {
  nkpmedical1: '#1976d2',
  nkpmedical2: '#d32f2f',
  nkpmedical3: '#388e3c',
  nkpmedical4: '#f57c00',
  nkpmedical5: '#7b1fa2',
  nkpmedical6: '#0288d1',
};
const FALLBACK_COLORS = ['#c62828', '#2e7d32', '#ef6c00', '#6a1b9a', '#1565c0', '#ad1457'];

const COMPARE_SUFFIX = '_compare';

export default function WPEnginePage() {
  const [accountUsage, setAccountUsage] = useState(null);
  const [accountUsageCompare, setAccountUsageCompare] = useState(null);
  const [installs, setInstalls] = useState(null);
  const [installsCompare, setInstallsCompare] = useState(null);

  const [start, setStart] = useState(firstOfMonthISO());
  const [end, setEnd] = useState(todayISO());

  const [compareEnabled, setCompareEnabled] = useState(false);
  // Default compare = the period of equal length immediately before [start, end]
  const defaultCompare = useMemo(() => {
    const span = daysBetween(start, end);
    const compareEnd = isoDateAdd(start, -1);
    const compareStart = isoDateAdd(compareEnd, -(span - 1));
    return { compareStart, compareEnd };
  }, [start, end]);
  const [compareStart, setCompareStart] = useState(defaultCompare.compareStart);
  const [compareEnd, setCompareEnd] = useState(defaultCompare.compareEnd);

  // When the main range changes, update the compare defaults (only if the user hasn't been editing compare)
  const [compareEdited, setCompareEdited] = useState(false);
  useEffect(() => {
    if (!compareEdited) {
      setCompareStart(defaultCompare.compareStart);
      setCompareEnd(defaultCompare.compareEnd);
    }
  }, [defaultCompare.compareStart, defaultCompare.compareEnd, compareEdited]);

  // Sort state for the top 10 table
  const [sortBy, setSortBy] = useState('period_total_bytes');  // 'period_total_bytes' | 'pct_change'
  const [sortDir, setSortDir] = useState('desc');

  // Fetch current period
  useEffect(() => {
    let cancelled = false;
    setAccountUsage(null);
    api.get(`/wpengine/usage/by-account?start=${start}&end=${end}`)
      .then((res) => { if (!cancelled) setAccountUsage(res.data); })
      .catch(() => { if (!cancelled) setAccountUsage({ accounts: [] }); });
    return () => { cancelled = true; };
  }, [start, end]);

  useEffect(() => {
    let cancelled = false;
    setInstalls(null);
    api.get(`/wpengine/installs?start=${start}&end=${end}`)
      .then((res) => { if (!cancelled) setInstalls(res.data); })
      .catch(() => { if (!cancelled) setInstalls([]); });
    return () => { cancelled = true; };
  }, [start, end]);

  // Fetch compare period
  useEffect(() => {
    if (!compareEnabled) { setAccountUsageCompare(null); return; }
    let cancelled = false;
    setAccountUsageCompare(null);
    api.get(`/wpengine/usage/by-account?start=${compareStart}&end=${compareEnd}`)
      .then((res) => { if (!cancelled) setAccountUsageCompare(res.data); })
      .catch(() => { if (!cancelled) setAccountUsageCompare({ accounts: [] }); });
    return () => { cancelled = true; };
  }, [compareEnabled, compareStart, compareEnd]);

  useEffect(() => {
    if (!compareEnabled) { setInstallsCompare(null); return; }
    let cancelled = false;
    setInstallsCompare(null);
    api.get(`/wpengine/installs?start=${compareStart}&end=${compareEnd}`)
      .then((res) => { if (!cancelled) setInstallsCompare(res.data); })
      .catch(() => { if (!cancelled) setInstallsCompare([]); });
    return () => { cancelled = true; };
  }, [compareEnabled, compareStart, compareEnd]);

  // Build chart data using relative day index so two periods align on the x-axis
  const chartRows = useMemo(() => {
    if (!accountUsage) return [];
    const rows = [];
    const span = Math.max(
      ...(accountUsage.accounts?.[0]?.days?.length || 0) > 0 ? [accountUsage.accounts[0].days.length] : [0],
      (compareEnabled && accountUsageCompare?.accounts?.[0]?.days?.length) || 0
    );
    for (let i = 0; i < span; i++) {
      const row = { dayIndex: i + 1 };
      // Current period
      for (const acct of accountUsage.accounts || []) {
        const day = (acct.days || [])[i];
        if (day) {
          row[acct.account_name] = day.network_total_bytes;
          row[`${acct.account_name}__date`] = day.date;
        }
      }
      // Compare period
      if (compareEnabled && accountUsageCompare) {
        for (const acct of accountUsageCompare.accounts || []) {
          const day = (acct.days || [])[i];
          if (day) {
            row[`${acct.account_name}${COMPARE_SUFFIX}`] = day.network_total_bytes;
            row[`${acct.account_name}${COMPARE_SUFFIX}__date`] = day.date;
          }
        }
      }
      rows.push(row);
    }
    return rows;
  }, [accountUsage, accountUsageCompare, compareEnabled]);

  const accountNames = useMemo(
    () => (accountUsage?.accounts || []).map((a) => a.account_name),
    [accountUsage]
  );

  const accountTotals = useMemo(() => {
    const totals = {};
    if (accountUsage) {
      for (const a of accountUsage.accounts || []) {
        totals[a.account_name] = (a.days || []).reduce((s, d) => s + (d.network_total_bytes || 0), 0);
      }
    }
    return totals;
  }, [accountUsage]);

  const accountTotalsCompare = useMemo(() => {
    const totals = {};
    if (accountUsageCompare) {
      for (const a of accountUsageCompare.accounts || []) {
        totals[a.account_name] = (a.days || []).reduce((s, d) => s + (d.network_total_bytes || 0), 0);
      }
    }
    return totals;
  }, [accountUsageCompare]);

  // Top 10 sites — include compare bandwidth and % change
  const top10Installs = useMemo(() => {
    if (!installs) return [];
    const compareById = {};
    for (const i of installsCompare || []) compareById[i.install_id] = i;
    const enriched = installs.map((i) => {
      const c = compareById[i.install_id];
      const priorBytes = c ? c.period_total_bytes : null;
      return {
        ...i,
        prior_period_bytes: priorBytes,
        pct_change: formatPct(i.period_total_bytes, priorBytes),
      };
    });
    const sorted = enriched.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      const sign = sortDir === 'desc' ? -1 : 1;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      return (av - bv) * sign;
    });
    return sorted.slice(0, 10);
  }, [installs, installsCompare, sortBy, sortDir]);

  const colorFor = (name, idx) => ACCOUNT_COLORS[name] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}>
          WP Engine
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField label="Start" type="date" size="small" value={start}
            onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="End" type="date" size="small" value={end}
            onChange={(e) => setEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>
      </Box>

      {/* Compare toolbar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Switch checked={compareEnabled} onChange={(e) => setCompareEnabled(e.target.checked)} />}
            label="Compare to another date range"
          />
          {compareEnabled && (
            <>
              <TextField label="Compare start" type="date" size="small" value={compareStart}
                onChange={(e) => { setCompareStart(e.target.value); setCompareEdited(true); }}
                InputLabelProps={{ shrink: true }} />
              <TextField label="Compare end" type="date" size="small" value={compareEnd}
                onChange={(e) => { setCompareEnd(e.target.value); setCompareEdited(true); }}
                InputLabelProps={{ shrink: true }} />
              <Typography variant="caption" color="text.secondary">
                Default: same-length period immediately before the selected range. Edit to override.
              </Typography>
            </>
          )}
        </Box>
      </Paper>

      {/* Per-account chart */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Daily bandwidth per server (sum across installs)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {start} → {end}{compareEnabled ? `  vs  ${compareStart} → ${compareEnd}` : ''}
          </Typography>
        </Box>
        {accountUsage === null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 6, color: 'text.secondary' }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading server usage…</Typography>
          </Box>
        ) : (accountUsage.accounts || []).length === 0 ? (
          <Alert severity="info">No data in this date range.</Alert>
        ) : (
          <>
            <Box sx={{ width: '100%', height: 420 }}>
              <ResponsiveContainer>
                <LineChart data={chartRows} margin={{ top: 8, right: 16, left: 16, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                  <XAxis
                    dataKey="dayIndex"
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Day in period', position: 'insideBottom', offset: -5, style: { fontSize: 11, fill: '#666' } }}
                  />
                  <YAxis tickFormatter={formatBytes} tick={{ fontSize: 12 }} width={80} />
                  <Tooltip
                    formatter={(value, name) => [value === null ? '—' : formatBytes(value), name.replace(COMPARE_SUFFIX, ' (prior)')]}
                    labelFormatter={(dayIdx) => `Day ${dayIdx}`}
                  />
                  <Legend
                    formatter={(value) => value.endsWith(COMPARE_SUFFIX) ? value.replace(COMPARE_SUFFIX, ' (prior)') : value}
                  />
                  {accountNames.map((name, i) => (
                    <Line key={name} type="monotone" dataKey={name}
                      stroke={colorFor(name, i)} dot={false} connectNulls={false} strokeWidth={2}
                    />
                  ))}
                  {compareEnabled && accountNames.map((name, i) => (
                    <Line key={`${name}${COMPARE_SUFFIX}`} type="monotone" dataKey={`${name}${COMPARE_SUFFIX}`}
                      stroke={colorFor(name, i)} dot={false} connectNulls={false} strokeWidth={1.5}
                      strokeDasharray="4 4" opacity={0.7}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>

            {/* Per-account totals (with compare delta when enabled) */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              {accountNames.map((name, i) => {
                const curr = accountTotals[name];
                const prior = compareEnabled ? accountTotalsCompare[name] : null;
                const pct = compareEnabled ? formatPct(curr, prior) : null;
                return (
                  <Chip
                    key={name}
                    size="small"
                    variant="outlined"
                    label={
                      <span>
                        <strong>{name}:</strong> {formatBytes(curr)}
                        {compareEnabled && (
                          <>
                            {' '}<span style={{ color: '#888' }}>vs {formatBytes(prior)}</span>
                            {pct !== null && (
                              <span style={{ marginLeft: 6, color: pct > 1 ? '#c62828' : pct < -1 ? '#2e7d32' : '#666' }}>
                                {pctLabel(pct)}
                              </span>
                            )}
                          </>
                        )}
                      </span>
                    }
                    sx={{ borderColor: colorFor(name, i), color: colorFor(name, i) }}
                  />
                );
              })}
            </Box>
          </>
        )}
      </Paper>

      {/* Top 10 sites */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Top 10 sites by bandwidth ({start} → {end})
        </Typography>
        {installs === null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, color: 'text.secondary' }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading sites…</Typography>
          </Box>
        ) : top10Installs.length === 0 ? (
          <Alert severity="info">No site-level data in this range yet.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Install</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Primary domain</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'period_total_bytes'}
                    direction={sortBy === 'period_total_bytes' ? sortDir : 'desc'}
                    onClick={() => toggleSort('period_total_bytes')}
                  >
                    Bandwidth
                  </TableSortLabel>
                </TableCell>
                {compareEnabled && <TableCell align="right">Prior</TableCell>}
                {compareEnabled && (
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortBy === 'pct_change'}
                      direction={sortBy === 'pct_change' ? sortDir : 'desc'}
                      onClick={() => toggleSort('pct_change')}
                    >
                      % change
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell align="right">Latest visits</TableCell>
                <TableCell align="right">Storage (files)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {top10Installs.map((i, idx) => (
                <TableRow key={i.install_id} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{i.install_name}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={i.account_name}
                      sx={{ borderColor: colorFor(i.account_name, 0), color: colorFor(i.account_name, 0) }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{i.primary_domain || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{formatBytes(i.period_total_bytes)}</TableCell>
                  {compareEnabled && <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatBytes(i.prior_period_bytes)}</TableCell>}
                  {compareEnabled && (
                    <TableCell align="right" sx={{ color: pctColor(i.pct_change), fontWeight: 500 }}>
                      {pctLabel(i.pct_change)}
                    </TableCell>
                  )}
                  <TableCell align="right">{formatNumber(i.latest?.visit_count)}</TableCell>
                  <TableCell align="right">{formatBytes(i.latest?.storage_file_bytes)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
